// Sport/gender/age-band point lookup for Protsaahan district-stage skill
// tests. Distinct from fitnessScoring.js (the 8-13 battery test): skill
// tests are sport-specific, cover ages 14-23, and use age *ranges*
// (14-17, 17-19, 19-21, 21-23) rather than single-year buckets, matching
// the official charts. Keep in sync with
// uk_module_admin_api/app/Library/UddyamaanSkillTestScoring.php.
//
// Bands overlap at their edges as printed on the official chart (e.g. 17 is
// the top of one band and the bottom of the next) -- an applicant on a
// boundary year takes the lower/earlier band, since bands are ordered
// ascending and the first match wins.
export const AGE_REFERENCE_DATE = '2026-07-01';

// Only athletics/male provided so far. Add sibling sport keys, and a
// 'female' key alongside 'male', once those charts are supplied -- no other
// code changes needed.
const TABLES = {
  athletics: {
    male: {
      bound_jump: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 5.50, points: 1 }, { min: 6.31, points: 2 }, { min: 7.11, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 5.80, points: 1 }, { min: 6.61, points: 2 }, { min: 7.41, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 6.20, points: 1 }, { min: 7.01, points: 2 }, { min: 7.71, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 6.70, points: 1 }, { min: 7.51, points: 2 }, { min: 8.31, points: 3 }] },
        ],
      },
      medicine_ball_throw: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 9.00, points: 1 }, { min: 13.01, points: 2 }, { min: 17.01, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 11.00, points: 1 }, { min: 15.01, points: 2 }, { min: 19.01, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 13.00, points: 1 }, { min: 16.01, points: 2 }, { min: 20.01, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 15.00, points: 1 }, { min: 17.01, points: 2 }, { min: 21.01, points: 3 }] },
        ],
      },
      cooper_test: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 2000, points: 1 }, { min: 2401, points: 2 }, { min: 2601, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 2200, points: 1 }, { min: 2601, points: 2 }, { min: 2901, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 2400, points: 1 }, { min: 2601, points: 2 }, { min: 2901, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 2500, points: 1 }, { min: 2801, points: 2 }, { min: 3101, points: 3 }] },
        ],
      },
      vertical_jump: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 48, points: 1 }, { min: 53, points: 2 }, { min: 57, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 52, points: 1 }, { min: 57, points: 2 }, { min: 62, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 56, points: 1 }, { min: 61, points: 2 }, { min: 66, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 60, points: 1 }, { min: 65, points: 2 }, { min: 70, points: 3 }] },
        ],
      },
      // Thresholds in seconds: 30s / 1min / 1:30min. Identical across all
      // four age bands per the official chart.
      single_leg_balance: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 30, points: 0.5 }, { min: 60, points: 1.0 }, { min: 90, points: 1.5 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 30, points: 0.5 }, { min: 60, points: 1.0 }, { min: 90, points: 1.5 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 30, points: 0.5 }, { min: 60, points: 1.0 }, { min: 90, points: 1.5 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 30, points: 0.5 }, { min: 60, points: 1.0 }, { min: 90, points: 1.5 }] },
        ],
      },
    },
    female: {
      bound_jump: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 4.30, points: 1 }, { min: 4.91, points: 2 }, { min: 5.51, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 4.80, points: 1 }, { min: 5.41, points: 2 }, { min: 6.01, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 5.30, points: 1 }, { min: 5.91, points: 2 }, { min: 6.51, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 5.80, points: 1 }, { min: 6.41, points: 2 }, { min: 7.01, points: 3 }] },
        ],
      },
      medicine_ball_throw: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 6.00, points: 1 }, { min: 9.01, points: 2 }, { min: 12.01, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 8.00, points: 1 }, { min: 11.01, points: 2 }, { min: 14.01, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 10.00, points: 1 }, { min: 13.01, points: 2 }, { min: 16.01, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 12.00, points: 1 }, { min: 15.01, points: 2 }, { min: 18.01, points: 3 }] },
        ],
      },
      cooper_test: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 1500, points: 1 }, { min: 1801, points: 2 }, { min: 2101, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 1700, points: 1 }, { min: 1901, points: 2 }, { min: 2201, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 1900, points: 1 }, { min: 2101, points: 2 }, { min: 2401, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 2100, points: 1 }, { min: 2401, points: 2 }, { min: 2601, points: 3 }] },
        ],
      },
      vertical_jump: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 43, points: 1 }, { min: 46, points: 2 }, { min: 54, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 47, points: 1 }, { min: 50, points: 2 }, { min: 55, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 51, points: 1 }, { min: 54, points: 2 }, { min: 57, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 55, points: 1 }, { min: 58, points: 2 }, { min: 60, points: 3 }] },
        ],
      },
      // Thresholds in seconds: 30s / 45s / 1min. Identical across all four
      // age bands per the official chart. Girls' scale is integer 1/2/3
      // points, unlike boys' 0.5/1.0/1.5.
      single_leg_balance: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 30, points: 1 }, { min: 45, points: 2 }, { min: 60, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 30, points: 1 }, { min: 45, points: 2 }, { min: 60, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 30, points: 1 }, { min: 45, points: 2 }, { min: 60, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 30, points: 1 }, { min: 45, points: 2 }, { min: 60, points: 3 }] },
        ],
      },
    },
  },
  basketball: {
    male: {
      // Rep counts in a fixed time window; the window itself shortens at
      // older age bands (01 min -> 47 sec etc.) per the official chart, but
      // that's a test-protocol detail, not a scored value -- only the
      // resulting rep count is recorded/scored.
      dribble_test: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 2, points: 1 }, { min: 3, points: 2 }, { min: 5, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
        ],
      },
      wall_pass_test: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 4, points: 1 }, { min: 9, points: 2 }, { min: 16, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 7, points: 1 }, { min: 11, points: 2 }, { min: 19, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 7, points: 1 }, { min: 11, points: 2 }, { min: 19, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 7, points: 1 }, { min: 11, points: 2 }, { min: 19, points: 3 }] },
        ],
      },
      full_court_layup: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
          // Chart tier-3 cell for this band read "7-8" (same as tier 2),
          // inconsistent with the other three bands' "9-10" — confirmed a
          // chart typo, corrected to 9 here.
          { min_age: 19, max_age: 21, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
        ],
      },
      speed_spot_shooting: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 3, points: 1 }, { min: 5, points: 2 }, { min: 8, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 3, points: 1 }, { min: 6, points: 2 }, { min: 9, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 4, points: 1 }, { min: 6, points: 2 }, { min: 9, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 4, points: 1 }, { min: 6, points: 2 }, { min: 9, points: 3 }] },
        ],
      },
      agility_skill_test: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 5, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 4, points: 1 }, { min: 5, points: 2 }, { min: 6, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 5, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 5, points: 3 }] },
        ],
      },
    },
    female: {
      dribble_test: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 2, points: 1 }, { min: 3, points: 2 }, { min: 5, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 2, points: 1 }, { min: 3, points: 2 }, { min: 5, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
        ],
      },
      wall_pass_test: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 13, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 7, points: 1 }, { min: 11, points: 2 }, { min: 19, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 7, points: 1 }, { min: 11, points: 2 }, { min: 17, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 7, points: 1 }, { min: 11, points: 2 }, { min: 18, points: 3 }] },
        ],
      },
      full_court_layup: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 4, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
        ],
      },
      speed_spot_shooting: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 3, points: 1 }, { min: 5, points: 2 }, { min: 8, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 3, points: 1 }, { min: 5, points: 2 }, { min: 8, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 4, points: 1 }, { min: 6, points: 2 }, { min: 9, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 4, points: 1 }, { min: 6, points: 2 }, { min: 9, points: 3 }] },
        ],
      },
      agility_skill_test: {
        direction: 'higher_is_better',
        age_bands: [
          { min_age: 14, max_age: 17, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 5, points: 3 }] },
          { min_age: 17, max_age: 19, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 5, points: 3 }] },
          { min_age: 19, max_age: 21, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 5, points: 3 }] },
          { min_age: 21, max_age: 23, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 5, points: 3 }] },
        ],
      },
    },
  },
  boxing: {
    male: {
      // Unlike athletics/basketball, boxing has no raw measurement (no
      // times/distances/rep counts) -- the assessor directly rates each
      // parameter 1/2/3 by observed technique quality. Tiers are identical
      // across all four age bands and every parameter: raw rating IS the
      // point value.
      onguard_footwork: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
      straight_punch: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
      shadow_boxing: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
      punching_bag: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
      punching_pad: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
    },
    // Identical to 'male' -- girls' chart has the same 1/2/3 direct rating,
    // same across every age band and parameter.
    female: {
      onguard_footwork: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
      straight_punch: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
      shadow_boxing: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
      punching_bag: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
      punching_pad: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }, { min: 2, points: 2 }, { min: 3, points: 3 }] },
      ] },
    },
  },
  football: {
    male: {
      // Kick-the-ball-for-accuracy and shooting-at-target are each 3
      // separate pass/fail attempts (right/middle/left, or attempt 1/2/3)
      // at a fixed target distance for the age band -- the distance itself
      // isn't a scored threshold, hit = 1pt, miss = 0, same shape as
      // boxing's rating fields but capped at 1 instead of 3.
      kick_accuracy_right: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_accuracy_middle: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_accuracy_left: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      // Timed -- faster is better, first lower_is_better skill-test
      // parameter (see pointsFromTiers).
      dribbling: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 15, points: 1 }, { max: 14, points: 2 }, { max: 13, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 14, points: 1 }, { max: 13, points: 2 }, { max: 12, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 13, points: 1 }, { max: 12, points: 2 }, { max: 11, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 13, points: 1 }, { max: 12, points: 2 }, { max: 11, points: 3 }] },
      ] },
      lofted_kick: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 25, points: 1 }, { min: 30, points: 2 }, { min: 35, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 35, points: 1 }, { min: 40, points: 2 }, { min: 45, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 40, points: 1 }, { min: 45, points: 2 }, { min: 50, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 40, points: 1 }, { min: 45, points: 2 }, { min: 50, points: 3 }] },
      ] },
      juggling: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 5, points: 1 }, { min: 10, points: 2 }, { min: 15, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 10, points: 1 }, { min: 15, points: 2 }, { min: 20, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 15, points: 1 }, { min: 20, points: 2 }, { min: 25, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 15, points: 1 }, { min: 20, points: 2 }, { min: 25, points: 3 }] },
      ] },
      shooting_at_target_1: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      shooting_at_target_2: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      shooting_at_target_3: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
    },
    female: {
      kick_accuracy_right: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_accuracy_middle: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_accuracy_left: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      dribbling: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 20, points: 1 }, { max: 19, points: 2 }, { max: 18, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 19, points: 1 }, { max: 18, points: 2 }, { max: 17, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 18, points: 1 }, { max: 17, points: 2 }, { max: 16, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 18, points: 1 }, { max: 17, points: 2 }, { max: 16, points: 3 }] },
      ] },
      lofted_kick: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 20, points: 1 }, { min: 25, points: 2 }, { min: 30, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 25, points: 1 }, { min: 30, points: 2 }, { min: 35, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 30, points: 1 }, { min: 35, points: 2 }, { min: 40, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 30, points: 1 }, { min: 35, points: 2 }, { min: 40, points: 3 }] },
      ] },
      juggling: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 5, points: 1 }, { min: 7, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 7, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 10, points: 1 }, { min: 12, points: 2 }, { min: 15, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 10, points: 1 }, { min: 12, points: 2 }, { min: 15, points: 3 }] },
      ] },
      shooting_at_target_1: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      shooting_at_target_2: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      shooting_at_target_3: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 1, points: 1 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 1, points: 1 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 1, points: 1 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
    },
  },
  hockey: {
    male: {
      zigzag_rolling: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 5, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 7, points: 1 }, { min: 9, points: 2 }, { min: 11, points: 3 }] },
      ] },
      hitting: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 4, points: 1 }, { min: 5, points: 2 }, { min: 7, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 5, points: 1 }, { min: 6, points: 2 }, { min: 8, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 6, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
      ] },
      push_pass: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 2, points: 1 }, { min: 3, points: 2 }, { min: 5, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 4, points: 1 }, { min: 5, points: 2 }, { min: 7, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 5, points: 1 }, { min: 6, points: 2 }, { min: 8, points: 3 }] },
      ] },
      scoop_overhead: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 30, points: 1 }, { min: 36, points: 2 }, { min: 40, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 35, points: 1 }, { min: 41, points: 2 }, { min: 46, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 41, points: 1 }, { min: 46, points: 2 }, { min: 51, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 46, points: 1 }, { min: 51, points: 2 }, { min: 56, points: 3 }] },
      ] },
      // Timed 50m dribble -- faster is better (lower_is_better).
      dribbling: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 20.0, points: 1 }, { max: 19.0, points: 2 }, { max: 18.0, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 19.0, points: 1 }, { max: 18.0, points: 2 }, { max: 17.0, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 17.51, points: 1 }, { max: 16.75, points: 2 }, { max: 16.0, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 16.0, points: 1 }, { max: 15.50, points: 2 }, { max: 14.50, points: 3 }] },
      ] },
    },
    // Identical to 'male'.
    female: {
      zigzag_rolling: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 5, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 7, points: 1 }, { min: 9, points: 2 }, { min: 11, points: 3 }] },
      ] },
      hitting: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 2, points: 1 }, { min: 3, points: 2 }, { min: 5, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 4, points: 1 }, { min: 5, points: 2 }, { min: 7, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 5, points: 1 }, { min: 6, points: 2 }, { min: 8, points: 3 }] },
      ] },
      push_pass: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 2, points: 1 }, { min: 3, points: 2 }, { min: 5, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 3, points: 1 }, { min: 4, points: 2 }, { min: 6, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 4, points: 1 }, { min: 5, points: 2 }, { min: 7, points: 3 }] },
      ] },
      scoop_overhead: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 15, points: 1 }, { min: 20, points: 2 }, { min: 25, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 20, points: 1 }, { min: 25, points: 2 }, { min: 30, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 25, points: 1 }, { min: 30, points: 2 }, { min: 35, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 30, points: 1 }, { min: 35, points: 2 }, { min: 40, points: 3 }] },
      ] },
      dribbling: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 28.8, points: 1 }, { max: 27.0, points: 2 }, { max: 26.0, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 27.0, points: 1 }, { max: 26.0, points: 2 }, { max: 25.0, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 25.0, points: 1 }, { max: 24.0, points: 2 }, { max: 23.0, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 23.0, points: 1 }, { max: 22.0, points: 2 }, { max: 21.0, points: 3 }] },
      ] },
    },
  },
  judo: {
    male: {
      // Rep counts in 20 sec per technique, higher_is_better. Chart labels
      // the lowest tier "X or fewer" rather than a lower bound -- since
      // reps can't be negative, that's just min=0: anyone who attempts the
      // technique gets at least 1pt, distinguishing "attempted, low reps"
      // from no data.
      uchi_komi: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 21, points: 2 }, { min: 24, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 23, points: 2 }, { min: 26, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 25, points: 2 }, { min: 28, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 27, points: 2 }, { min: 30, points: 3 }] },
      ] },
      ukemi: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 9, points: 2 }, { min: 13, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 10, points: 2 }, { min: 14, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 11, points: 2 }, { min: 15, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 12, points: 2 }, { min: 16, points: 3 }] },
      ] },
      yuko_ukemi: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 9, points: 2 }, { min: 13, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 10, points: 2 }, { min: 14, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 11, points: 2 }, { min: 15, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 12, points: 2 }, { min: 16, points: 3 }] },
      ] },
      nage_komi: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 9, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 10, points: 2 }, { min: 13, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 11, points: 2 }, { min: 14, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 12, points: 2 }, { min: 15, points: 3 }] },
      ] },
      pull_up: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 11, points: 2 }, { min: 14, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 13, points: 2 }, { min: 16, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 15, points: 2 }, { min: 18, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 17, points: 2 }, { min: 20, points: 3 }] },
      ] },
    },
    female: {
      uchi_komi: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 19, points: 2 }, { min: 22, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 21, points: 2 }, { min: 24, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 23, points: 2 }, { min: 26, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 25, points: 2 }, { min: 28, points: 3 }] },
      ] },
      ukemi: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 8, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 9, points: 2 }, { min: 13, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 10, points: 2 }, { min: 14, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 11, points: 2 }, { min: 15, points: 3 }] },
      ] },
      yuko_ukemi: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 8, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 9, points: 2 }, { min: 13, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 10, points: 2 }, { min: 14, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 11, points: 2 }, { min: 15, points: 3 }] },
      ] },
      nage_komi: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 7, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 9, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 10, points: 2 }, { min: 13, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 11, points: 2 }, { min: 14, points: 3 }] },
      ] },
      // "Pull-up Hanging", 30 sec.
      pull_up: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 8, points: 2 }, { min: 14.01, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 10, points: 2 }, { min: 16.01, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 12, points: 2 }, { min: 18.01, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 14, points: 2 }, { min: 20, points: 3 }] },
      ] },
    },
  },
  kabaddi: {
    male: {
      // Rep counts in 30 sec per drill, higher_is_better.
      touch_with_toe: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 8, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 8, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 14, points: 1 }, { min: 16, points: 2 }, { min: 18, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 18, points: 1 }, { min: 20, points: 2 }, { min: 22, points: 3 }] },
      ] },
      touch_with_hand: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 3, points: 1 }, { min: 5, points: 2 }, { min: 7, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 4, points: 1 }, { min: 6, points: 2 }, { min: 8, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 7, points: 1 }, { min: 9, points: 2 }, { min: 11, points: 3 }] },
      ] },
      ankle_catching: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 12, points: 1 }, { min: 14, points: 2 }, { min: 16, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 14, points: 1 }, { min: 16, points: 2 }, { min: 18, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 14, points: 1 }, { min: 16, points: 2 }, { min: 18, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 16, points: 1 }, { min: 18, points: 2 }, { min: 20, points: 3 }] },
      ] },
      chain_hold: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 8, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 10, points: 1 }, { min: 12, points: 2 }, { min: 14, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 12, points: 1 }, { min: 14, points: 2 }, { min: 16, points: 3 }] },
      ] },
      crossing_bonus_line: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 5, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
      ] },
    },
    // Girls' chart swaps "Ankle Catching" for a distinct drill, "Ankle
    // Hold" -- different technique, so it's its own param key (ankle_hold)
    // rather than reusing male's ankle_catching.
    female: {
      touch_with_toe: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 5, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 7, points: 1 }, { min: 9, points: 2 }, { min: 11, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 8, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 8, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
      ] },
      touch_with_hand: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 3, points: 1 }, { min: 5, points: 2 }, { min: 7, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 4, points: 1 }, { min: 6, points: 2 }, { min: 8, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 5, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 5, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
      ] },
      chain_hold: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 7, points: 1 }, { min: 9, points: 2 }, { min: 11, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 8, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 8, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
      ] },
      ankle_hold: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 4, points: 1 }, { min: 6, points: 2 }, { min: 8, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 8, points: 1 }, { min: 10, points: 2 }, { min: 12, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 10, points: 1 }, { min: 12, points: 2 }, { min: 14, points: 3 }] },
      ] },
      crossing_bonus_line: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 4, points: 1 }, { min: 6, points: 2 }, { min: 8, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 5, points: 1 }, { min: 7, points: 2 }, { min: 9, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 6, points: 1 }, { min: 8, points: 2 }, { min: 10, points: 3 }] },
      ] },
    },
  },
  karate: {
    male: {
      // Kihon (basics): a timed drill (e.g. 50 punches), full marks (1.0)
      // if completed within the age band's time threshold, half marks
      // (0.5) otherwise -- the chart never shows a 0, so the 0.5 tier's max
      // uses a large sentinel to act as an unconditional floor for anyone
      // who attempted it.
      punch: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 15, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 15, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 12, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 12, points: 1.0 }] },
      ] },
      kick: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 20, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 20, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 18, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 18, points: 1.0 }] },
      ] },
      block: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 10, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 10, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
      ] },
      punch_combination: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
      ] },
      kick_combination: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 12, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 12, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 12, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 12, points: 1.0 }] },
      ] },
      // Kata (forms) and Kumite (sparring techniques): pass/fail, fixed
      // point value per technique, no age-band breakdown on the chart --
      // one band spanning the whole 14-23 range.
      kata_basic: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.5 }] },
      ] },
      kata_senior: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.5 }] },
      ] },
      kata_advance: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 2.0 }] },
      ] },
      gyaku_zuki: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
      kizami_zuki: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
      mae_geri: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
      mawashi_geri: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
      ura_mawashi_geri: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
    },
    // Kata/Kumite identical to 'male'; only Kihon thresholds differ.
    female: {
      punch: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 15, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 15, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 13, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 13, points: 1.0 }] },
      ] },
      kick: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 22, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 22, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 20, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 20, points: 1.0 }] },
      ] },
      block: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 10, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 10, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
      ] },
      punch_combination: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 8, points: 1.0 }] },
      ] },
      kick_combination: { direction: 'lower_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ max: 9999, points: 0.5 }, { max: 14, points: 1.0 }] },
        { min_age: 17, max_age: 19, tiers: [{ max: 9999, points: 0.5 }, { max: 14, points: 1.0 }] },
        { min_age: 19, max_age: 21, tiers: [{ max: 9999, points: 0.5 }, { max: 14, points: 1.0 }] },
        { min_age: 21, max_age: 23, tiers: [{ max: 9999, points: 0.5 }, { max: 14, points: 1.0 }] },
      ] },
      kata_basic: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.5 }] },
      ] },
      kata_senior: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.5 }] },
      ] },
      kata_advance: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 2.0 }] },
      ] },
      gyaku_zuki: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
      kizami_zuki: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
      mae_geri: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
      mawashi_geri: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
      ura_mawashi_geri: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1.0 }] },
      ] },
    },
  },
  taekwondo: {
    male: {
      // Groups 1/2/5 each list 3 named technique variants done within the
      // SAME time window per age band (e.g. "10 sec Normal / 10 sec Speed /
      // 10 sec Knee High") -- these are 3 separate pass/fail drills, not 3
      // tiers of one measurement (same pattern as football's kick-accuracy
      // columns), so the time itself is protocol context, not a scored
      // value; each is worth a flat 1pt, single band spanning 14-23.
      basic_stance_normal: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      basic_stance_speed: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      basic_stance_knee_high: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_basic: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_fera: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_advance: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      // Hand Technique: 3 separate 5-rep pass/fail drills, fixed across
      // every age band.
      hand_punch: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      hand_block: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      hand_strike: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      // Reaction Test: the one genuinely tiered measurement in this sport
      // -- confirmed as printed, a SLOWER reaction (more seconds) scores
      // higher, opposite of every timed test elsewhere in this dataset.
      // Same tiers across all four age bands.
      reaction_test: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 6, points: 2 }, { min: 11, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 6, points: 2 }, { min: 11, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 6, points: 2 }, { min: 11, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 6, points: 2 }, { min: 11, points: 3 }] },
      ] },
      target_kick_normal: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      target_kick_face: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      target_kick_advance: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
    },
    // Identical to 'male' -- girls' chart has the same thresholds
    // throughout, confirmed against the actual girls' image.
    female: {
      basic_stance_normal: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      basic_stance_speed: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      basic_stance_knee_high: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_basic: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_fera: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      kick_advance: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      hand_punch: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      hand_block: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      hand_strike: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      reaction_test: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 17, tiers: [{ min: 0, points: 1 }, { min: 6, points: 2 }, { min: 11, points: 3 }] },
        { min_age: 17, max_age: 19, tiers: [{ min: 0, points: 1 }, { min: 6, points: 2 }, { min: 11, points: 3 }] },
        { min_age: 19, max_age: 21, tiers: [{ min: 0, points: 1 }, { min: 6, points: 2 }, { min: 11, points: 3 }] },
        { min_age: 21, max_age: 23, tiers: [{ min: 0, points: 1 }, { min: 6, points: 2 }, { min: 11, points: 3 }] },
      ] },
      target_kick_normal: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      target_kick_face: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
      target_kick_advance: { direction: 'higher_is_better', age_bands: [
        { min_age: 14, max_age: 23, tiers: [{ min: 1, points: 1 }] },
      ] },
    },
  },
  // Other sports added as siblings of 'athletics'/'basketball'/'boxing'/'football'/'hockey'/'judo'/'kabaddi'/'karate'/'taekwondo' once their charts are supplied.
};

export function ageForBand(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const ref = new Date(AGE_REFERENCE_DATE);
  if (isNaN(birth.getTime())) return null;
  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) age -= 1;
  return age;
}

function bandFor(ageBands, age) {
  if (age === null || age === undefined) return null;
  return ageBands.find(b => age >= b.min_age && age <= b.max_age) || null;
}

// higher_is_better tiers use 'min' (value >= min); lower_is_better tiers
// (e.g. a timed dribble -- faster is better) use 'max' (value <= max).
// Either way, the highest-points matching tier wins, not the first match.
function pointsFromTiers(tiers, direction, value) {
  if (value === null || value === undefined || Number.isNaN(value)) return 0;
  let best = 0;
  for (const tier of tiers) {
    const matches = direction === 'lower_is_better' ? value <= tier.max : value >= tier.min;
    if (matches && tier.points > best) best = tier.points;
  }
  return best;
}

const toNumberOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

// UI metadata (label/unit) per parameter, keyed by sport -- kept separate
// from TABLES since it's presentational, not scoring data. Parameter sets
// differ per sport (athletics measures distances/time, basketball counts
// reps in a fixed window), so this must never be a single flat list shared
// across sports.
const FIELD_META = {
  athletics: {
    bound_jump: { label: '2-Step Bound & Jump', unit: 'm' },
    medicine_ball_throw: { label: 'Overhead Backward Medicine Ball Throw (1kg)', unit: 'm' },
    cooper_test: { label: 'Cooper Test (12 min)', unit: 'm' },
    vertical_jump: { label: 'Vertical Jump', unit: 'cm' },
    single_leg_balance: { label: 'Single Leg Balance', unit: 'sec' },
  },
  basketball: {
    dribble_test: { label: 'Dribble Test', unit: 'reps' },
    wall_pass_test: { label: 'Wall Pass Test', unit: 'reps' },
    full_court_layup: { label: 'Full Court 10 Lay-up', unit: 'reps' },
    speed_spot_shooting: { label: 'Speed Spot Shooting', unit: 'reps' },
    agility_skill_test: { label: 'Agility Skill Test', unit: 'reps' },
  },
  boxing: {
    // No unit -- these are direct 1/2/3 assessor ratings, not a measured
    // raw value against a threshold.
    onguard_footwork: { label: 'Onguard / Footwork', unit: 'rating (1-3)', inputProps: { min: '1', max: '3', step: '1' } },
    straight_punch: { label: 'Straight Punch', unit: 'rating (1-3)', inputProps: { min: '1', max: '3', step: '1' } },
    shadow_boxing: { label: 'Shadow Boxing', unit: 'rating (1-3)', inputProps: { min: '1', max: '3', step: '1' } },
    punching_bag: { label: 'Punching Bag', unit: 'rating (1-3)', inputProps: { min: '1', max: '3', step: '1' } },
    punching_pad: { label: 'Punching Pad', unit: 'rating (1-3)', inputProps: { min: '1', max: '3', step: '1' } },
  },
  football: {
    // Pass/fail attempts -- 1 for a hit, 0 for a miss, at the fixed target
    // distance printed for the applicant's age band on the chart.
    kick_accuracy_right: { label: 'Kick for Accuracy — Right', unit: 'hit (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    kick_accuracy_middle: { label: 'Kick for Accuracy — Middle', unit: 'hit (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    kick_accuracy_left: { label: 'Kick for Accuracy — Left', unit: 'hit (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    dribbling: { label: 'Dribbling', unit: 'sec' },
    lofted_kick: { label: 'Lofted Kick', unit: 'm' },
    juggling: { label: 'Juggling', unit: 'm' },
    shooting_at_target_1: { label: 'Shooting at Target — Attempt 1', unit: 'hit (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    shooting_at_target_2: { label: 'Shooting at Target — Attempt 2', unit: 'hit (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    shooting_at_target_3: { label: 'Shooting at Target — Attempt 3', unit: 'hit (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
  },
  hockey: {
    zigzag_rolling: { label: 'Zigzag Rolling (30 sec)', unit: 'reps' },
    hitting: { label: 'Hitting (of 10 balls)', unit: 'hits' },
    push_pass: { label: 'Push-Pass (of 10 balls)', unit: 'hits' },
    scoop_overhead: { label: 'Scoop / Overhead', unit: 'm' },
    dribbling: { label: 'Dribbling (50m)', unit: 'sec' },
  },
  judo: {
    uchi_komi: { label: 'Uchi Komi (20 sec)', unit: 'reps' },
    ukemi: { label: 'Ukemi (20 sec)', unit: 'reps' },
    yuko_ukemi: { label: 'Yuko Ukemi (20 sec)', unit: 'reps' },
    nage_komi: { label: 'Nage Komi (20 sec)', unit: 'reps' },
    pull_up: { label: 'Pull-up', unit: 'reps' },
  },
  kabaddi: {
    touch_with_toe: { label: 'Touch with Toe (30 sec)', unit: 'reps' },
    touch_with_hand: { label: 'Touch with Hand (30 sec)', unit: 'reps' },
    ankle_catching: { label: 'Ankle Catching (30 sec)', unit: 'reps' },
    ankle_hold: { label: 'Ankle Hold (30 sec)', unit: 'reps' },
    chain_hold: { label: 'Chain Hold (30 sec)', unit: 'reps' },
    crossing_bonus_line: { label: 'Crossing the Bonus Line (30 sec)', unit: 'reps' },
  },
  karate: {
    punch: { label: 'Punch (50 reps, timed)', unit: 'sec' },
    kick: { label: 'Kick (20 reps, timed)', unit: 'sec' },
    block: { label: 'Block (20 reps, timed)', unit: 'sec' },
    punch_combination: { label: 'Punch Combination (10 reps, timed)', unit: 'sec' },
    kick_combination: { label: 'Kick Combination (10 reps, timed)', unit: 'sec' },
    // Pass/fail -- 1 for demonstrated correctly, 0 for not.
    kata_basic: { label: 'Kata — Basic', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    kata_senior: { label: 'Kata — Senior', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    kata_advance: { label: 'Kata — Advance', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    gyaku_zuki: { label: 'Kumite — Gyaku Zuki', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    kizami_zuki: { label: 'Kumite — Kizami Zuki', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    mae_geri: { label: 'Kumite — Mae Geri', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    mawashi_geri: { label: 'Kumite — Mawashi Geri', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    ura_mawashi_geri: { label: 'Kumite — Ura Mawashi Geri', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
  },
  taekwondo: {
    // Pass/fail -- 1 for completed within the age band's time window, 0 for not.
    basic_stance_normal: { label: 'Basic Stance — Normal', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    basic_stance_speed: { label: 'Basic Stance — Speed', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    basic_stance_knee_high: { label: 'Basic Stance — Knee High', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    kick_basic: { label: 'Kick — Basic', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    kick_fera: { label: 'Kick — Fera', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    kick_advance: { label: 'Kick — Advance', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    hand_punch: { label: 'Hand Technique — Punch (5 reps)', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    hand_block: { label: 'Hand Technique — Block (5 reps)', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    hand_strike: { label: 'Hand Technique — Strike (5 reps)', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    reaction_test: { label: 'Reaction Test', unit: 'sec' },
    target_kick_normal: { label: 'Target Kicking — Normal', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    target_kick_face: { label: 'Target Kicking — Face', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
    target_kick_advance: { label: 'Target Kicking — Advance', unit: 'pass (0/1)', inputProps: { min: '0', max: '1', step: '1' } },
  },
};

// The parameter list for a sport/gender is read off the scoring table
// itself (not a hardcoded list) so it can never drift out of sync with what
// scoreAllSkillTests actually scores.
function paramsFor(sport, gender) {
  const sportTable = (TABLES[sport] && TABLES[sport][gender]) || null;
  return sportTable ? Object.keys(sportTable) : [];
}

// Ordered {key, label, unit} list for rendering the Skill Test section of a
// given sport/gender. Empty when that combo has no chart loaded yet.
export function getSkillTestFields(sport, gender) {
  const meta = FIELD_META[sport] || {};
  return paramsFor(sport, gender).map(key => ({ key, inputProps: {}, ...(meta[key] || { label: key, unit: '' }) }));
}

// Computes points for all skill-test parameters of the given sport from raw
// measurement form values + gender/dob. `raw` uses the same field names as
// the parameter keys for that sport (see FIELD_META).
export function scoreAllSkillTests(sport, gender, dob, raw) {
  const age = ageForBand(dob);
  const sportTable = (TABLES[sport] && TABLES[sport][gender]) || null;

  const result = {};
  let total = 0;
  for (const param of paramsFor(sport, gender)) {
    const table = sportTable[param];
    const value = toNumberOrNull(raw[param]);
    const band = bandFor(table.age_bands, age);
    const points = band ? pointsFromTiers(band.tiers, table.direction, value) : '';
    result[`${param}_points`] = points;
    total += Number(points) || 0;
  }
  result.total_points = total;
  return result;
}

// Max achievable skill-test total for a sport/gender -- varies by gender
// (e.g. boys' single_leg_balance tops out at 1.5 pts, girls' at 3), so
// callers must not assume a fixed denominator like "out of 12".
export function maxSkillPoints(sport, gender) {
  const sportTable = (TABLES[sport] && TABLES[sport][gender]) || null;
  if (!sportTable) return 0;
  return paramsFor(sport, gender).reduce((sum, param) => {
    const table = sportTable[param];
    const bestTierPoints = Math.max(...table.age_bands[0].tiers.map(t => t.points));
    return sum + bestTierPoints;
  }, 0);
}
